import React, { useEffect, useRef, useState } from "react";
import BackgroundLayout from "../Components/BackgroundLayout";
import Background from "../Components/Background";
import Navbar from "../Components/Navbar";
import classes from "./Home.module.css";
import { motion } from "framer-motion";
import SectionDragCards from "../Components/Drag-Cards/SectionDragCards";
import { useAppDispatch, useAppSelector } from "../Store/hooks";
import {
  useGetDataByGenreIdQuery,
  useGetFreeMoviesQuery,
  useGetFreeSeriesQuery,
  useGetShortzQuery,
  useGetTrendngDataQuery,
} from "../Slices/httpSlice";
import { useNavigate } from "react-router-dom";
import MovieCard, { MovieData } from "../Components/Drag-Cards/MovieCard";
import NavbarMobile from "../Components/NavbarMobile";
import BottomNavbar from "../Components/BottomNavbar";
import SearchModal from "../Components/SearchModal";
import ReactPlayer from "react-player";
import WatchlistModal from "../Components/WatchlistModal";
import UserRatingsModal from "../Components/UserRatingsModal";
import ProfileModal from "../Components/ProfileModal";
import Drawer from "../Components/Drawer";
import DragCardsTitle from "../Components/Drag-Cards/DragCardsTitle";
import { setFreeToWatchMovies } from "../Slices/freeToWatchSlice";
import FreeMediaDragSection from "../Components/Drag-Cards/FreeMediaDragSection";
import SkeletonCardsGrid from "../Components/SkeletonCardsGrid";
import ErrorCard from "../Components/Drag-Cards/ErrorCard";

export type TrendingArrayType = {
  backdrop_path: string;
  id: number;
  title: string;
  name?: string;
  original_name?: string;
  original_title: string;
  overview: string;
  poster_path: string;
  media_type?: string;
  adult: boolean;
  original_language: string;
  genre_ids: string[];
  popularity: number;
  release_date: string;
  video: boolean;
  vote_average: number;
  vote_count: number;
}[];

const Home = () => {
  const [leftConstraint, setLeftConstraint] = useState<number>(0);

  const [data, setData] = useState<any>([]);

  const navigate = useNavigate();

  useEffect(() => {
    const updateConstraints = () => {
      // 3 cards 250px 3 * 250px
      // if window width is 250 * 3 + 110px than we will add the spring effect..
      if (window.innerWidth < 3 * 250 + 110) {
        // hide all the cards but left some part of the 2nd last card.. lets say 75px
        // setLeftConstraint((3 - 1) * 250 + (3 - 1) * 20 - 75);

        // logic to implement the drag to upto how much screen
        // total cards * width of card + total cards * gap between cards
        setLeftConstraint(3 * 250 + 3 * 20 - window.innerWidth);
      } else {
        setLeftConstraint(0);
      }
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, []);

  const { freeToWatchMovies } = useAppSelector(
    (state) => state.freeToWatchSlice
  );
  const dispatch = useAppDispatch();

  const {
    data: freeMoviesData,
    refetch: freeMoviesRefetch,
    isFetching: freeMoviesLoading,
    error: freeMoviesError,
  } = useGetFreeMoviesQuery(
    {},
    {
      skip: !freeToWatchMovies,
    }
  );

  const {
    data: freeSeriesData,
    refetch: freeSeriesRefetch,
    isFetching: freeSeriesLoading,
    error: freeSeriesError,
  } = useGetFreeSeriesQuery({}, { skip: freeToWatchMovies });

  useEffect(() => {
    if (freeToWatchMovies) {
      freeMoviesRefetch();
    } else {
      freeSeriesRefetch();
    }
  }, [freeToWatchMovies]);

  const {
    trendingMovies,
    adventureMovies,
    comedyMovies,
    scificMovies,
    thrillerMovies,
    animationMovies,
    documentaryMovies,
    mysteryMovies,
    westernMovies,
  } = useAppSelector((state) => state.mediaTypeSlice);

  const {
    data: trending,
    refetch: trendingRefetch,
    isFetching: trendingLoading,
    error: trendingError,
  } = useGetTrendngDataQuery(trendingMovies ? "movie" : "tv");

  const {
    data: adventure,
    refetch: adventureRefetch,
    isFetching: adventureLoading,
    error: adventureError,
  } = useGetDataByGenreIdQuery({
    media: adventureMovies ? "movie" : "tv",
    genreId: adventureMovies ? 12 : 10759,
    pageNumber: 1,
  });

  const {
    data: comedy,
    refetch: comedyRefetch,
    isFetching: comedyLoading,
    error: comedyError,
  } = useGetDataByGenreIdQuery({
    media: comedyMovies ? "movie" : "tv",
    genreId: 35,
    pageNumber: 1,
  });

  const {
    data: scific,
    refetch: scificRefetch,
    isFetching: scificLoading,
    error: scificError,
  } = useGetDataByGenreIdQuery({
    media: scificMovies ? "movie" : "tv",
    genreId: scificMovies ? 878 : 10765,
    pageNumber: 1,
  });

  const {
    data: thriller,
    refetch: thrillerRefetch,
    isFetching: thrillerLoading,
    error: thrillerError,
  } = useGetDataByGenreIdQuery({
    media: thrillerMovies ? "movie" : "tv",
    genreId: thrillerMovies ? 53 : 80,
    pageNumber: 1,
  });

  const {
    data: animation,
    refetch: animationRefetch,
    isFetching: animationLoading,
    error: animationError,
  } = useGetDataByGenreIdQuery({
    media: animationMovies ? "movie" : "tv",
    genreId: 16,
    pageNumber: 1,
  });

  const {
    data: mystery,
    refetch: mysteryRefetch,
    isFetching: mysteryLoading,
    error: mysteryError,
  } = useGetDataByGenreIdQuery({
    media: mysteryMovies ? "movie" : "tv",
    genreId: 9648,
    pageNumber: 1,
  });

  const {
    data: documentary,
    refetch: documentaryRefetch,
    isFetching: documentaryLoading,
    error: documentaryError,
  } = useGetDataByGenreIdQuery({
    media: documentaryMovies ? "movie" : "tv",
    genreId: 99,
    pageNumber: 1,
  });

  const {
    data: western,
    refetch: westernRefetch,
    isFetching: westernLoading,
    error: westernError,
  } = useGetDataByGenreIdQuery({
    media: westernMovies ? "movie" : "tv",
    genreId: 37,
    pageNumber: 1,
  });

  useEffect(() => {
    trendingRefetch();
  }, [trendingMovies]);

  useEffect(() => {
    adventureRefetch();
  }, [adventureMovies]);

  useEffect(() => {
    comedyRefetch();
  }, [comedyMovies]);

  useEffect(() => {
    scificRefetch();
  }, [scificMovies]);

  useEffect(() => {
    thrillerRefetch();
  }, [thrillerMovies]);

  useEffect(() => {
    animationRefetch();
  }, [animationMovies]);

  useEffect(() => {
    mysteryRefetch();
  }, [mysteryMovies]);

  useEffect(() => {
    documentaryRefetch();
  }, [documentaryMovies]);

  useEffect(() => {
    westernRefetch();
  }, [westernMovies]);

  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    setStartPosition({ x: event.clientX, y: event.clientY });
    setIsDragging(false);
  };

  const handleMouseUp = (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string
  ) => {
    const distance = Math.sqrt(
      Math.pow(event.clientX - startPosition.x, 2) +
        Math.pow(event.clientY - startPosition.y, 2)
    );
    if (distance < 5 && !isDragging) {
      navigate(path, {
        state: {
          reset: true,
        },
      });
    }
  };

  const handleMouseMove = () => {
    setIsDragging(true);
  };

  const {
    data: shortzData,
    isFetching: shortzLoading,
    refetch: shortzRefetch,
    isError: shortzError,
  } = useGetShortzQuery(null);

  const [shortzDataArray, setShortzDataArray] = useState<any[]>([]);

  useEffect(() => {
    if (shortzDataArray.length < 4) {
      shortzRefetch();
    }
  }, [shortzDataArray, shortzError]);
  useEffect(() => {
    if (shortzData?.result) {
      setShortzDataArray((prevValue) =>
        prevValue ? [...prevValue, shortzData.result] : [shortzData.result]
      );
    }
  }, [shortzData]);

  console.log(shortzDataArray, "sda");

  return (
    <>
      <BackgroundLayout>
        <NavbarMobile />
        <Navbar activeLink={1} />
        <div className={classes.landing_page}>
          <div className={classes.landing_page_intro}>
            <p className={classes.primary_text}>
              Explore our wide variety of Categories
            </p>
            <p className={classes.secondary_text}>
              Whether you are looking for Movies or TV shows we have got you
              covered.
            </p>
          </div>

          <motion.div
            className={classes.main_grid}
            drag="x"
            dragConstraints={{
              top: 0,
              left: -leftConstraint,
              right: 0,
              bottom: 0,
            }}
            transition={{
              type: "spring",
              damping: 100,
              stiffness: 100,
            }}
          >
            <button
              type="button"
              className={classes.card}
              onMouseDown={(event) => handleMouseDown(event)}
              onMouseUp={(event) => handleMouseUp(event, "/movies")}
              onMouseMove={handleMouseMove}
            >
              <div className={classes.card_grid}>
                {movies?.map((data, idx) => (
                  <div className={classes.card_grid_item} key={idx}>
                    <img
                      src={data}
                      alt="movie"
                      className={classes.card_grid_item_image}
                    />
                  </div>
                ))}
              </div>
              <div className={classes.card_actions}>
                <p className={classes.card_title}>Explore Movies</p>
                <button className={classes.explore_btn}>
                  <i className={`pi pi-arrow-right ${classes.explore_icon}`} />
                </button>
              </div>
            </button>

            <button
              className={classes.card}
              type="button"
              onMouseDown={(event) => handleMouseDown(event)}
              onMouseUp={(event) => handleMouseUp(event, "/series")}
              onMouseMove={handleMouseMove}
            >
              <div className={classes.card_grid}>
                {series?.map((data, idx) => (
                  <div className={classes.card_grid_item} key={idx}>
                    <img
                      src={data}
                      alt="movie"
                      className={classes.card_grid_item_image}
                    />
                  </div>
                ))}
              </div>
              <div className={classes.card_actions}>
                <p className={classes.card_title}>Explore Series</p>
                <button className={classes.explore_btn}>
                  <i className={`pi pi-arrow-right ${classes.explore_icon}`} />
                </button>
              </div>
            </button>

            <button
              type="button"
              className={classes.card}
              onMouseDown={(event) => handleMouseDown(event)}
              onMouseUp={(event) => handleMouseUp(event, "/shortz")}
              onMouseMove={handleMouseMove}
            >
              <div className={classes.card_grid}>
                {shortzDataArray?.slice(0, 4).map((data: any, idx: number) => (
                  <div className={classes.card_grid_item} key={idx}>
                    <ReactPlayer
                      url={`https://www.youtube.com/watch?v=${data?.key}`}
                      controls={true}
                      playing={true}
                      loop={true}
                      muted={true}
                      playsinline={true}
                      width="100%"
                      className={classes.card_grid_item_video}
                      config={{
                        youtube: {
                          playerVars: {
                            showinfo: 0,
                            rel: 0,
                            modestbranding: 1,
                          },
                        },
                      }}
                    />
                  </div>
                ))}
                {shortzDataArray.length !== 4 &&
                  Array.from({ length: 4 - shortzDataArray.length }).map(
                    (_, index) => (
                      <motion.div
                        className={classes.card_grid_item}
                        key={index}
                        initial={{
                          background:
                            "linear-gradient(45deg,hsl(0deg 0% 14%) 0%,hsl(0deg 0% 14%) 20%,hsl(0deg 0% 14%) 29%,hsl(0deg 0% 14%) 36%,hsl(0deg 0% 14%) 43%,hsl(0deg 0% 14%) 50%,hsl(228deg 3% 12%) 57%,hsl(228deg 5% 11%) 64%,hsl(228deg 8% 9%) 71%,hsl(228deg 11% 8%) 80%,hsl(228deg 14% 7%) 100%)",
                        }}
                        animate={{
                          background: [
                            "linear-gradient(45deg, hsl(228deg 14% 7%) 0%, hsl(228deg 11% 8%) 20%, hsl(228deg 8% 9%) 29%, hsl(228deg 5% 11%) 36%, hsl(228deg 3% 12%) 43%, hsl(0deg 0% 14%) 50%, hsl(228deg 3% 12%) 57%, hsl(228deg 5% 11%) 64%, hsl(228deg 8% 9%) 71%, hsl(228deg 11% 8%) 80%, hsl(228deg 14% 7%) 100%)",
                            "linear-gradient(45deg,hsl(0deg 0% 14%) 0%,hsl(0deg 0% 14%) 20%,hsl(0deg 0% 14%) 29%,hsl(0deg 0% 14%) 36%,hsl(0deg 0% 14%) 43%,hsl(0deg 0% 14%) 50%,hsl(228deg 3% 12%) 57%,hsl(228deg 5% 11%) 64%,hsl(228deg 8% 9%) 71%,hsl(228deg 11% 8%) 80%,hsl(228deg 14% 7%) 100%)",
                            "linear-gradient(45deg,hsl(228deg 14% 7%) 0%,hsl(228deg 11% 8%) 20%,hsl(228deg 8% 9%) 29%,hsl(228deg 5% 11%) 36%,hsl(228deg 3% 12%) 43%,hsl(0deg 0% 14%) 50%,hsl(0deg 0% 14%) 57%,hsl(0deg 0% 14%) 64%,hsl(0deg 0% 14%) 71%,hsl(0deg 0% 14%) 80%,hsl(0deg 0% 14%) 100%)",
                          ],
                        }}
                        transition={{
                          duration: 2,
                          repeatType: "mirror",
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      ></motion.div>
                    )
                  )}
              </div>
              <div className={classes.card_actions}>
                <p className={classes.card_title}>Explore Shortz</p>
                <button className={classes.explore_btn}>
                  <i className={`pi pi-arrow-right ${classes.explore_icon}`} />
                </button>
              </div>
            </button>
          </motion.div>
        </div>
      </BackgroundLayout>
      <Background>
        <div className={classes.section_drag_cards}>
          <div className={classes.section_cards}>
            <DragCardsTitle
              title="Free To Watch"
              switchActive={freeToWatchMovies}
              onMovieClick={() => dispatch(setFreeToWatchMovies(true))}
              onSeriesClick={() => dispatch(setFreeToWatchMovies(false))}
              hideFilter={false}
            />

            {freeMoviesLoading || freeSeriesLoading ? (
              <SkeletonCardsGrid />
            ) : freeMoviesError || freeSeriesError ? (
              <ErrorCard error={freeMoviesError ?? freeSeriesError} />
            ) : (
              <FreeMediaDragSection
                data={freeToWatchMovies ? freeMoviesData : freeSeriesData}
                media_type={freeToWatchMovies ? "movies" : "series"}
              />
            )}
          </div>
        </div>

        <SectionDragCards
          hideFilter={false}
          trending={trending}
          adventure={adventure}
          comedy={comedy}
          scific={scific}
          thriller={thriller}
          animation={animation}
          mystery={mystery}
          documentary={documentary}
          western={western}
          trendingLoading={trendingLoading}
          adventureLoading={adventureLoading}
          comedyLoading={comedyLoading}
          scificLoading={scificLoading}
          thrillerLoading={thrillerLoading}
          animationLoading={animationLoading}
          documentaryLoading={documentaryLoading}
          westernLoading={westernLoading}
          mysteryLoading={mysteryLoading}
          trendingError={trendingError}
          adventureError={adventureError}
          comedyError={comedyError}
          scificError={scificError}
          thrillerError={thrillerError}
          animationError={animationError}
          mysteryError={mysteryError}
          documentaryError={documentaryError}
          westernError={westernError}
          checkAndNavigate={true}
          navigateToMovies={false}
          navigateToSeries={false}
        />
      </Background>
      <SearchModal />

      <WatchlistModal />
      <UserRatingsModal />
      <ProfileModal />
      <Drawer />
      <BottomNavbar activeLink={1} />
    </>
  );
};

export default Home;

const movies: string[] = [
  "/assets/cards-images/movie1.jpg",
  "/assets/cards-images/movie2.jpg",
  "/assets/cards-images/movie3.jpg",
  "/assets/cards-images/movie4.jpg",
];

const series: string[] = [
  "/assets/cards-images/series1.jpg",
  "/assets/cards-images/series2.jpg",
  "/assets/cards-images/series3.jpg",
  "/assets/cards-images/series4.jpg",
];

const shortz: string[] = [
  "/assets/cards-images/movie1.jpg",
  "/assets/cards-images/movie2.jpg",
  "/assets/cards-images/movie3.jpg",
  "/assets/cards-images/movie4.jpg",
];
